import styles from './sales-offer-cart.module.scss';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CreateHeaders, HeaderSizeEnum, LicenseTypeEnum, NumberSelect, PrimaryButton, textHeader, Typography, Dialog, DialogTitle, DialogContent, DialogActions, GrayOutlinedButton, SecondaryButton, List, ListItem, ListItemIcon, ListItemText, currencyHeader } from '@frontend-workspace/react-shared-lib';
import WebkuaTable from '../../webkua-table/webkua-table';
import { IProductGroup } from '../../_models/product-group';
import { customerRecurringPriceRenderer } from '../sales-offer-table-renderers/sales-offer-table-renderers';
import { IOfferProductGroup } from '../../_models/offer-product';
import { useSalesOfferContext } from '@frontend-workspace/webkua-lib';

/* eslint-disable-next-line */
export interface SalesOfferCartProps {
  open: boolean,
  onComplete: () => void,
  currentGroup?: IOfferProductGroup,
  currentProductGroup: IProductGroup[],
  onCancel: () => void
}

export function SalesOfferCart(props: SalesOfferCartProps) {
  const { t, i18n } = useTranslation();
  const { formData, updateFormData } = useSalesOfferContext();
  const [activeLicenseTypes, setActiveLicenseTypes] = useState<string[]>([LicenseTypeEnum.NLM, LicenseTypeEnum.NAMED_USER]);

  const seatsCellRenderer = (cellprops: any, params?: any) => {
    return <NumberSelect
      value={cellprops.value}
      onValueChange={(e) => {
        const updatedSeats = formData.cart.map(product => {
          if (product.itemInfoId === cellprops.data.itemInfoId) {
            return { ...product, newSeats: e < 0 ? 0 : e, indicators: [] };
          }
          return { ...product, indicators: [] };
        });
        updateFormData('cart', updatedSeats);
      }}
      supportNegative={false}
    />
  };

  const toggleNamedUser = () => { setActiveLicenseTypes(activeLicenseTypes.includes(LicenseTypeEnum.NAMED_USER) ? activeLicenseTypes.filter(type => type !== LicenseTypeEnum.NAMED_USER) : [...activeLicenseTypes, LicenseTypeEnum.NAMED_USER]) };
  const toggleNlm = () => { setActiveLicenseTypes(activeLicenseTypes.includes(LicenseTypeEnum.NLM) ? activeLicenseTypes.filter(type => type !== LicenseTypeEnum.NLM) : [...activeLicenseTypes, LicenseTypeEnum.NLM]) };

  const filteredCart = formData.cart.filter(item => activeLicenseTypes.includes(item.licenseType) && props.currentProductGroup.some(i => i.itemInfoId === item.itemInfoId));

  return (
    <Dialog
      open={props.open}
      onClose={props.onComplete}
      maxWidth="xxl"
      fullWidth={true}>
      <DialogTitle className='mb-4'>
        {t("shop.cartOverview")}
      </DialogTitle>
      <DialogContent>
        {props.currentGroup &&
          <List className='mb-3' dense disablePadding>
            <ListItem disableGutters>
              <ListItemIcon>
                <img src={props.currentGroup.iconUrl} alt="" width={40} height={40} className='me-3' />
              </ListItemIcon>
              <ListItemText primary={<span className='MuiTypography-body2'>{props.currentGroup.productGroupName}</span>} />
            </ListItem>
          </List>
        }

        {formData.cart.length === 0 &&
          <div style={{ marginTop: '100px' }}>
            <Typography variant='body1' className='d-flex justify-content-center mt-5 mb-1'>{t('shop.noItemsInCart')}</Typography>
            <Typography variant='body6' className='d-flex justify-content-center mb-5'>{t('shop.addProductFirst')}</Typography>
            <div className='d-flex justify-content-center mb-5'>
              <PrimaryButton
                variant="outlined"
                onClick={() => props.onCancel()}
                className="minButtonWidth me-3 my-2">
                {t("shop.backToWebshop")}
              </PrimaryButton>
            </div>
          </div>
        }

        {formData.cart.length > 0 &&
          <Box
            component="div"
            className="pb-4 pb-xl-0 mb-4">
            <WebkuaTable
              tableFilters={[
                {
                  checked: activeLicenseTypes.includes(LicenseTypeEnum.NLM),
                  label: t('licenseType.nlm'),
                  onChange: toggleNlm,
                  disabled: !formData.cart.some(item => item.licenseType === LicenseTypeEnum.NLM)
                }, {
                  checked: activeLicenseTypes.includes(LicenseTypeEnum.NAMED_USER),
                  label: t('licenseType.namedUser'),
                  onChange: toggleNamedUser,
                  disabled: !formData.cart.some(item => item.licenseType === LicenseTypeEnum.NAMED_USER)
                }
              ]}
              headers={CreateHeaders([
                textHeader("newSeats", t("shop.addSeats"), HeaderSizeEnum.MEDIUM, {
                  renderer: seatsCellRenderer
                }),
                textHeader("currentSeats", t("shop.currentSeats"), HeaderSizeEnum.MEDIUM),
                textHeader("name", t("shop.productName"), HeaderSizeEnum.EXTRA_LARGE),
                textHeader("licenseType", t("shop.type"), HeaderSizeEnum.MEDIUM, {
                  renderer: (params: any) => {
                    return t(`licenseType.${params.value?.toLowerCase()}`)
                  }
                }),
                textHeader("licenseNumber", t("shop.licenseNumber"), HeaderSizeEnum.MEDIUM),
                currencyHeader("recurringPrice", t("shop.pricePerSeat"), HeaderSizeEnum.MEDIUM, {
                  cellStyle: { justifyContent: 'right' },
                  headerClass: "ag-right-aligned-header"
                }),
                currencyHeader("recurringPrice", t("shop.sumSubscription"), HeaderSizeEnum.LARGE, {
                  renderer: customerRecurringPriceRenderer,
                  cellStyle: { justifyContent: 'right' },
                  headerClass: "ag-right-aligned-header"
                }),
              ])}
              data={filteredCart}
              pageSize={999}
              hidePagination={true}
            />
          </Box>
        }
      </DialogContent>
      <DialogActions>
        <SecondaryButton
          variant="contained"
          size='large'
          onClick={props.onComplete}
          className='minButtonWidth'>
          {t("standard.complete")}
        </SecondaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={() => props.onCancel()}
          className='minButtonWidth'>
          {t("standard.back")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default SalesOfferCart;
